import React, { useState, useEffect } from 'react';
import { useSQLiteService } from '../hooks/useSQLiteService';
import { SQLiteService } from '../services/SQLiteService';
import styles from './DatabaseView.module.css';

interface DatabaseObject {
  object_id: string;
  parent_id?: string | null;
  object_type: string;
  is_tombstoned: boolean;
  dirty?: boolean;
  content_hash?: string | null;
  subtree_hash?: string | null;
  version_vector?: string | null;
  updated_at?: number | null;
}

interface DatabaseProperty {
  object_id: string;
  property: string;
  value: string;
  lww_counter: number;
  lww_random: number;
  logical_timestamp: number;
  replica_id: string;
  user_id: string;
  updated_at: number;
}

interface DatabaseViewProps {
  sqliteService?: SQLiteService | null;
  documentId?: string;
}

const DatabaseView: React.FC<DatabaseViewProps> = ({ sqliteService: propSqliteService, documentId }) => {
  const [activeTable, setActiveTable] = useState<'objects' | 'object_properties'>('objects');
  const [objects, setObjects] = useState<DatabaseObject[]>([]);
  const [properties, setProperties] = useState<DatabaseProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const hookSqliteService = useSQLiteService(documentId);
  const sqliteService = propSqliteService || hookSqliteService;

  const loadData = async () => {
    if (!sqliteService) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Load objects
      const objectsData = await sqliteService.getObjects();
      setObjects(objectsData || []);
      
      // Load properties
      const propertiesData = await sqliteService.getAllProperties();
      setProperties(propertiesData || []);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load database data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [sqliteService]);


  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString();
  };

  const formatJSON = (jsonString: any): string => {
    if (jsonString === null || jsonString === undefined) return '';
    const jsonStr = String(jsonString);
    try {
      return JSON.stringify(JSON.parse(jsonStr), null, 2);
    } catch {
      return jsonStr;
    }
  };

  const truncateText = (text: any, maxLength: number = 50): string => {
    if (text === null || text === undefined) return '—';
    const textStr = String(text); // Convert to string safely
    if (textStr.length <= maxLength) return textStr;
    return textStr.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className={styles.databaseLoading}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading database data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.databaseError}>
        <h3>Error loading database</h3>
        <p>{error}</p>
        <button onClick={loadData} className={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.databaseView}>

      {/* Table Tabs */}
      <div className={styles.tableTabs}>
        <button 
          className={`${styles.tableTab} ${activeTable === 'objects' ? styles.active : ''}`}
          onClick={() => setActiveTable('objects')}
        >
          Objects ({objects.length})
        </button>
        <button 
          className={`${styles.tableTab} ${activeTable === 'object_properties' ? styles.active : ''}`}
          onClick={() => setActiveTable('object_properties')}
        >
          Object Properties ({properties.length})
        </button>
      </div>


      {/* Tables */}
      {activeTable === 'objects' && (
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <h3>Objects Table</h3>
            <button onClick={loadData} className={styles.refreshBtn} title="Refresh">
              🔄
            </button>
          </div>
          {objects.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No objects in database</p>
              <small>Create a document to see objects here</small>
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Object ID</th>
                    <th>Parent ID</th>
                    <th>Type</th>
                    <th>Tombstoned</th>
                    <th>Dirty</th>
                    <th>Content Hash</th>
                    <th>Subtree Hash</th>
                    <th>Version Vector</th>
                    <th>Updated At</th>
                  </tr>
                </thead>
                <tbody>
                  {objects.map((obj, index) => (
                    <tr key={obj?.object_id || `obj-${index}`}>
                      <td className={styles.objectId}>{truncateText(obj.object_id)}</td>
                      <td>{truncateText(obj.parent_id)}</td>
                      <td className={styles.objectType}>{obj.object_type || '—'}</td>
                      <td className={obj.is_tombstoned ? styles.booleanTrue : styles.booleanFalse}>
                        {obj.is_tombstoned ? '✓' : '✗'}
                      </td>
                      <td className={obj.dirty ? styles.booleanTrue : styles.booleanFalse}>
                        {obj.dirty ? '✓' : '✗'}
                      </td>
                      <td className={styles.hash}>{truncateText(obj.content_hash, 12)}</td>
                      <td className={styles.hash}>{truncateText(obj.subtree_hash, 12)}</td>
                      <td className={styles.versionVector}>
                        {truncateText(formatJSON(obj.version_vector), 20)}
                      </td>
                      <td className={styles.timestamp}>{obj.updated_at ? formatTimestamp(obj.updated_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTable === 'object_properties' && (
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <h3>Object Properties Table</h3>
            <button onClick={loadData} className={styles.refreshBtn} title="Refresh">
              🔄
            </button>
          </div>
          {properties.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No properties in database</p>
              <small>Create a document to see properties here</small>
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Object ID</th>
                    <th>Property</th>
                    <th>Value</th>
                    <th>LWW Counter</th>
                    <th>LWW Random</th>
                    <th>Logical TS</th>
                    <th>Replica ID</th>
                    <th>User ID</th>
                    <th>Updated At</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((prop, index) => (
                    <tr key={`${prop?.object_id || 'unknown'}-${prop?.property || 'unknown'}-${index}`}>
                      <td className={styles.objectId}>{truncateText(prop.object_id)}</td>
                      <td className={styles.propertyName}>{prop.property || '—'}</td>
                      <td className={styles.propertyValue}>{truncateText(prop.value, 80)}</td>
                      <td className={styles.lwwCounter}>{prop.lww_counter || 0}</td>
                      <td className={styles.lwwRandom}>{prop.lww_random || 0}</td>
                      <td className={styles.logicalTimestamp}>{prop.logical_timestamp || 0}</td>
                      <td className={styles.replicaId}>{truncateText(prop.replica_id)}</td>
                      <td className={styles.userId}>{truncateText(prop.user_id)}</td>
                      <td className={styles.timestamp}>{prop.updated_at ? formatTimestamp(prop.updated_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default DatabaseView;
